import {
  AppBar,
  Box,
  Button,
  Divider,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Toolbar,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { LogOut, User } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import datlabLogo from '../../assets/datlab9_logo_small.svg';
import { checkTauriPresentSync } from '../../services/backend/isDesktop.js';
import { openLink } from '../../services/backend/openUrl.js';
import { checkForAppUpdate } from '../../services/backend/updater.js';
import { useAuthStore } from '../../stores/authStore.js';
import About from '../About.jsx';

const MENU_BAR_HEIGHT = '56px';

const MenuBar = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAnchors, setMenuAnchors] = useState({});
  const [userAnchor, setUserAnchor] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(false);

  // Navigation items — direct buttons for the main screens
  const navItems = [
    { label: 'Projects', path: '/projects' },
    { label: 'Analysis', path: '/analysis' },
    { label: 'Reports', path: '/reports' },
  ];

  if (checkTauriPresentSync()) {
    navItems.push({ label: 'Device', path: '/desktop-view' });
  }

  // Dropdown menus
  const dropdownMenus = [
    {
      label: 'Help',
      items: [
        { label: 'Documentation', action: 'docs' },
        { label: 'Check for Updates', action: 'checkForUpdates' },
        { label: 'divider', divider: true },
        { label: 'About', action: 'about' },
      ],
    },
  ];

  const openMenu = (menuLabel, el) => {
    setMenuAnchors((prev) => ({ ...prev, [menuLabel]: el }));
  };

  const closeMenu = (menuLabel) => {
    setMenuAnchors((prev) => ({ ...prev, [menuLabel]: null }));
  };

  const handleUserMenuOpen = (event) => {
    setUserAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };

  const handleMenuItemClick = (action) => {
    switch (action) {
      case 'docs':
        try {
          openLink('https://wiki.oroboros.at/index.php/DatLab-Manual');
        } finally {
          setMenuAnchors({});
        }
        break;
      case 'checkForUpdates':
        checkForAppUpdate({ manual: true });
        setMenuAnchors({});
        break;
      case 'about':
        setAboutOpen(true);
        setMenuAnchors({});
        break;
      default:
        break;
    }
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{ backgroundColor: 'transparent', boxShadow: 'none', height: MENU_BAR_HEIGHT }}
      >
        <Toolbar disableGutters sx={{ justifyContent: 'space-between', alignItems: 'start' }}>
          {/* Left side — logo + screen navigation + dropdown menus */}
          <div
            style={{ display: 'flex', gap: '24px', height: MENU_BAR_HEIGHT, alignItems: 'center' }}
          >
            {/* Logo */}
            <img
              src={datlabLogo}
              alt="DatLab Studio"
              style={{ height: 42, userSelect: 'none', marginRight: 4 }}
            />
            {/* Direct navigation buttons */}
            {navItems.map((item) => (
              <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  borderBottom:
                    location.pathname === item.path ? '2px solid #394b57' : '2px solid transparent',
                  borderRadius: 0,
                  paddingBottom: '2px',
                }}
              >
                {item.label}
              </Button>
            ))}

            {/* Dropdown menus (Help, etc.) */}
            {dropdownMenus.map((menu) => (
              <div key={menu.label} style={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  onClick={(e) => openMenu(menu.label, e.currentTarget)}
                  sx={{
                    borderBottom: '2px solid transparent',
                    borderRadius: 0,
                    paddingBottom: '2px',
                  }}
                >
                  {menu.label}
                </Button>
                <Popover
                  open={Boolean(menuAnchors[menu.label])}
                  anchorEl={menuAnchors[menu.label]}
                  onClose={() => closeMenu(menu.label)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  disableAutoFocus
                  disableEnforceFocus
                  disableRestoreFocus
                >
                  <MenuList autoFocusItem={false}>
                    {menu.items.map((item, index) =>
                      item.divider ? (
                        <Divider sx={{ mx: '12px' }} key={`${menu.label}-${index}`} />
                      ) : (
                        <MenuItem
                          key={`${menu.label}-${index}`}
                          onClick={() => handleMenuItemClick(item.action)}
                        >
                          <ListItemText>{item.label}</ListItemText>
                        </MenuItem>
                      ),
                    )}
                  </MenuList>
                </Popover>
              </div>
            ))}
          </div>

          {/* Right side — User menu */}
          <Button onClick={handleUserMenuOpen}>
            <User width={20} strokeWidth={1.5} style={{ marginRight: '6px' }} />
            {user?.name || user?.email || 'User'}
          </Button>
          <Popover
            open={Boolean(userAnchor)}
            anchorEl={userAnchor}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            slotProps={{
              paper: { onMouseLeave: handleUserMenuClose },
            }}
          >
            <MenuList autoFocusItem={false}>
              <MenuItem
                onClick={handleLogout}
                sx={{ justifyContent: 'space-between', padding: '8px 12px' }}
              >
                <ListItemText>Logout</ListItemText>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LogOut width={16} height={16} strokeWidth={1.5} />
                </Box>
              </MenuItem>
            </MenuList>
          </Popover>
        </Toolbar>
      </AppBar>
      <Dialog
        open={aboutOpen}
        onClose={() => setAboutOpen(false)}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: { background: 'transparent', boxShadow: 'none' },
          },
        }}
      >
        <About onClose={() => setAboutOpen(false)} />
      </Dialog>
    </>
  );
};

export default MenuBar;
